import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TPosProduct } from '../../pages/mdrProduct/types'
import { Fee, PlanDetail, OLIDetail, PlanViewByHardPos, PlanSubType } from 'models/mdrProduct.model'
import { Issuer } from 'models'
import { InsurancePartner } from 'models/insurance.model'

interface ProductMDRFees {
  [key: string]: {
    id: number
    value: number | string
    isInsurance?: boolean
    merchantSplit: number | string
    insurerSplit: number | string
    issuer?: number
    insurancePartner?: number
    insuranceSubPartner?: number
    isDisabled?: boolean
    disabledNonAdditionalType?: boolean
  }[]
}

interface ratesMDRFee {
  id: number
  cardSegment: string | null
  sellRate: number | string | null
  txnAmount: string
  capAmount: number | string | null
  isEdited: boolean
  rateType: number
  annualTurnover: string
  aggregaterKey1?: string | null
  aggregaterKey3?: string | null
  plateformFeeType?: string | null
  plateformFeeName?: string | null
  plateformFeeValue?: string | null
}

export interface IviewRatesMdrFeeData {
  [headingKey: string]: {
    [subHeadingKey: string]: ratesMDRFee[]
  }
}

export interface IviewRatesMdrFeeDataContainer {
  [key: string]: IviewRatesMdrFeeData
}
interface MdrProductState {
  posProducts: TPosProduct[]
  allIssuers: Issuer[]
  mainProductBasedOnPlan: PlanDetail | null
  customAdvancedMonth: number | null
  interchangePlan: number | null
  interchangePlanError: boolean
  interchangePlanDisabled: boolean
  ippEnabledToggles: Record<string, boolean>
  isCreateOliLoading: boolean
  viewRatesMdrFeeData: null | IviewRatesMdrFeeDataContainer
  productMdrFees: ProductMDRFees
  editPosProduct: {
    currentSelectPlanId: number | null
    selectedVasProducts: string[]
    currentSelectedAllPlan: number[]
    sfReferenceId?: string
    productImageUrl?: string
    productName?: string
    productQuantity: number
  } | null
  selectedPosProducts: { [key: string]: OLIDetail[] }
  isProductsLoading: boolean
  currentSelectedPosProduct: TPosProduct | null
  planList: PlanViewByHardPos[]
  planBenefitList: PlanSubType[]
  isPlansLoading: boolean
  currentPlan: string | null
  currentPlanBenefit: PlanSubType | null
  isPlanFeeListLoading: boolean
  availableSponsoredDevices: number
  totalSelectedPosProductsQuantity: number
  isEditFeesBottomSheetOpen: {
    isOpen: boolean
    fees: {
      isCustomValueLessThanFee: { [key: string]: boolean } | null
      otherFees: Fee[]
      rentalFees: Fee[]
    } | null
  }
  allInsurancePartners: InsurancePartner[]
  currentSettlementCategory: string
  currentAcquire: string
  currentSubscribe: string
  mainQuantity: number
  acquirers: { [productName: string]: string[] }
  remainingAcquirers: { [productName: string]: string[] }
  selectedProductsByVasNameMap: { [vasName: string]: PlanDetail[] }
  remainingProductsByVasNameMap: { [vasName: string]: PlanDetail[] }
  accessories: {
    name: string
    price: number
    quantity: number
  }[]
  posProductInfoForBottomSheet: {
    isOpen: boolean
    posProduct: TPosProduct | null
  }
  mccCode: string | null
  isFIUUMerchant: boolean
  fiuuOpportunityId: number | null
  fiuuOldPlanId: number | null
  fiuuOldPlanProducts: PlanDetail[] | null
  billingFrequency: string | null
  billingStartDate: string | null
  gtvAmount: number | null
  gtvScope: string | null
  isGtvCustomised: boolean
  selectedIppMerchantCategory: string | null
  selectedAecsCardMerchantCategory: string | null
  aecsCardCategoryValidationError: boolean
  merchantUsingExistingTerminal: boolean | null
  existingTerminalAcquirer: string | null
  existingTerminalRentalFrequency: string | null
  existingTerminalRentalAmount: number | null
  existingTerminalMDR: string | null
  isTaxable: boolean
  canFeeEdited: boolean
  isAdvanceMonthLocked: boolean
  oliVasExistence: {
    isPlanPresentInOli: boolean
    isQRPresentInOli: boolean
    isBnplPresentInOli: boolean
    isIppPresentInOli: boolean
    isCardPresentInOli: boolean
  }
}

const initialState: MdrProductState = {
  posProducts: [],
  allIssuers: [],
  editPosProduct: {
    currentSelectPlanId: null,
    selectedVasProducts: [],
    currentSelectedAllPlan: [],
    productQuantity: 0,
  },
  allInsurancePartners: [],
  viewRatesMdrFeeData: null,
  customAdvancedMonth: null,
  interchangePlan: null,
  interchangePlanError: false,
  interchangePlanDisabled: false,
  ippEnabledToggles: {},
  isCreateOliLoading: false,
  mainProductBasedOnPlan: null,
  selectedPosProducts: {},
  productMdrFees: {},
  isProductsLoading: false,
  currentSelectedPosProduct: null,
  planList: [],
  planBenefitList: [],
  isPlansLoading: false,
  currentPlan: null,
  currentPlanBenefit: null,
  currentSettlementCategory: 'Aggregator',
  currentAcquire: 'HDFC',
  currentSubscribe: 'One time',
  isPlanFeeListLoading: false,
  isEditFeesBottomSheetOpen: {
    isOpen: false,
    fees: null,
  },
  mainQuantity: 1,
  availableSponsoredDevices: 0,
  totalSelectedPosProductsQuantity: 0,
  acquirers: {},
  remainingAcquirers: {},
  selectedProductsByVasNameMap: {},
  remainingProductsByVasNameMap: {},
  // selectedVasPosProducts: {},
  accessories: [
    { name: 'Standee', price: 1999, quantity: 1 },
    { name: 'Base', price: 1999, quantity: 0 },
    // { name: 'Paper Roll Set', price: 999, quantity: 2 },
  ],
  posProductInfoForBottomSheet: {
    isOpen: false,
    posProduct: null,
  },
  mccCode: null,
  isFIUUMerchant: false,
  fiuuOpportunityId: null,
  fiuuOldPlanId: null,
  fiuuOldPlanProducts: null,
  billingFrequency: null,
  billingStartDate: null,
  gtvAmount: null,
  gtvScope: null,
  isGtvCustomised: false,
  selectedIppMerchantCategory: null,
  selectedAecsCardMerchantCategory: null,
  aecsCardCategoryValidationError: false,
  merchantUsingExistingTerminal: null,
  existingTerminalAcquirer: null,
  existingTerminalRentalFrequency: null,
  existingTerminalRentalAmount: null,
  existingTerminalMDR: null,
  isTaxable: true,
  canFeeEdited: true,
  isAdvanceMonthLocked: false,
  oliVasExistence: {
    isPlanPresentInOli: false,
    isQRPresentInOli: false,
    isBnplPresentInOli: false,
    isIppPresentInOli: false,
    isCardPresentInOli: false,
  },
}

const mdrProductSlice = createSlice({
  name: 'mdrProduct',
  initialState,
  reducers: {
    setProductsLoading(state, action: PayloadAction<boolean>) {
      state.isProductsLoading = action.payload
    },
    setIsCreateOliLoading(state, action: PayloadAction<boolean>) {
      state.isCreateOliLoading = action.payload
    },
    setPosProducts(state, action: PayloadAction<TPosProduct[]>) {
      state.posProducts = action.payload
    },
    setCustomAdvancedMonth(state, action: PayloadAction<number | null>) {
      state.customAdvancedMonth = action.payload
    },
    setInterchangePlan(state, action: PayloadAction<number | null>) {
      state.interchangePlan = action.payload
      state.interchangePlanError = false
    },
    setInterchangePlanError(state, action: PayloadAction<boolean>) {
      state.interchangePlanError = action.payload
    },
    setInterchangePlanDisabled(state, action: PayloadAction<boolean>) {
      state.interchangePlanDisabled = action.payload
    },
    setAllIssuers(state, action: PayloadAction<Issuer[]>) {
      state.allIssuers = action.payload
    },
    setEditPosProduct(
      state,
      action: PayloadAction<{
        currentSelectPlanId: number | null
        selectedVasProducts: string[]
        currentSelectedAllPlan: number[]
        sfReferenceId?: string
        productImageUrl?: string
        productName?: string
        productQuantity: number
      }>,
    ) {
      state.editPosProduct = action.payload
    },
    setProductMDRFees(state, action: PayloadAction<ProductMDRFees>) {
      state.productMdrFees = action.payload
    },
    setSelectedPosProducts(state, action: PayloadAction<{ [key: string]: OLIDetail[] }>) {
      state.selectedPosProducts = action.payload
    },

    setCurrentSelectedPosProduct(state, action: PayloadAction<TPosProduct | null>) {
      state.currentSelectedPosProduct = action.payload
    },
    setPlansLoading(state, action: PayloadAction<boolean>) {
      state.isPlansLoading = action.payload
    },
    setIsPlanFeeListLoading(state, action: PayloadAction<boolean>) {
      state.isPlanFeeListLoading = action.payload
    },
    setIsEditFeesBottomSheetOpen(
      state,
      action: PayloadAction<{
        isOpen: boolean
        fees: {
          isCustomValueLessThanFee: { [key: string]: boolean } | null
          otherFees: Fee[]
          rentalFees: Fee[]
        } | null
      }>,
    ) {
      state.isEditFeesBottomSheetOpen = action.payload
    },
    setPlanList(state, action: PayloadAction<PlanViewByHardPos[]>) {
      state.planList = action.payload
    },
    setPlanBenefitList(state, action: PayloadAction<PlanSubType[]>) {
      state.planBenefitList = action.payload
    },
    setCurrentPlan(state, action: PayloadAction<string | null>) {
      state.currentPlan = action.payload
    },
    setCurrentPlanBenefit(state, action: PayloadAction<PlanSubType | null>) {
      state.currentPlanBenefit = action.payload
    },
    setMainProductBasedOnPlan(state, action: PayloadAction<PlanDetail | null>) {
      state.mainProductBasedOnPlan = action.payload
    },
    setAcquirers(state, action: PayloadAction<{ [productName: string]: string[] }>) {
      state.acquirers = action.payload
    },
    setRemainingAcquirers(state, action: PayloadAction<{ [productName: string]: string[] }>) {
      state.remainingAcquirers = action.payload
    },
    setSelectedProductsByVasNameMap(
      state,
      action: PayloadAction<{ [vasName: string]: PlanDetail[] }>,
    ) {
      state.selectedProductsByVasNameMap = action.payload
    },

    setRemainingProductsByVasNameMap(
      state,
      action: PayloadAction<{ [vasName: string]: PlanDetail[] }>,
    ) {
      state.remainingProductsByVasNameMap = action.payload
    },

    setCurrentSettlementCategory(state, action: PayloadAction<string>) {
      state.currentSettlementCategory = action.payload
    },
    setCurrentAcquire(state, action: PayloadAction<string>) {
      state.currentAcquire = action.payload
    },
    setCurrentSubscribe(state, action: PayloadAction<string>) {
      state.currentSubscribe = action.payload
    },
    setAllInsurancePartners(state, action: PayloadAction<InsurancePartner[]>) {
      state.allInsurancePartners = action.payload
    },
    setViewRatesMdrFeeData(state, action: PayloadAction<null | IviewRatesMdrFeeDataContainer>) {
      state.viewRatesMdrFeeData = action.payload
    },

    updateMainQuantity(state, action: PayloadAction<number>) {
      state.mainQuantity = action.payload
    },
    updateAccessoryQuantity(state, action: PayloadAction<{ index: number; amount: number }>) {
      const { index, amount } = action.payload
      const newQuantity = state.accessories[index].quantity + amount
      if (newQuantity >= 0) {
        state.accessories[index].quantity = newQuantity
      }
    },
    setAccessories(
      state,
      action: PayloadAction<{ name: string; price: number; quantity: number }[]>,
    ) {
      state.accessories = action.payload
    },

    setPosProductInfoForBottomSheet(
      state,
      action: PayloadAction<{ isOpen: boolean; posProduct: TPosProduct | null }>,
    ) {
      state.posProductInfoForBottomSheet = action.payload
    },

    setMccCode(state, action: PayloadAction<string | null>) {
      state.mccCode = action.payload
    },

    setIsFIUUMerchant(state, action: PayloadAction<boolean>) {
      state.isFIUUMerchant = action.payload
    },

    setFiuuOpportunityId(state, action: PayloadAction<number | null>) {
      state.fiuuOpportunityId = action.payload
    },

    setFiuuOldPlanId(state, action: PayloadAction<number | null>) {
      state.fiuuOldPlanId = action.payload
    },

    setFiuuOldPlanProducts(state, action: PayloadAction<PlanDetail[] | null>) {
      state.fiuuOldPlanProducts = action.payload
    },

    setBillingFrequency(state, action: PayloadAction<string | null>) {
      state.billingFrequency = action.payload
    },

    setBillingStartDate(state, action: PayloadAction<string | null>) {
      state.billingStartDate = action.payload
    },

    setGtvAmount(state, action: PayloadAction<number | null>) {
      state.gtvAmount = action.payload
    },

    setGtvScope(state, action: PayloadAction<string | null>) {
      state.gtvScope = action.payload
    },

    setIsGtvCustomised(state, action: PayloadAction<boolean>) {
      state.isGtvCustomised = action.payload
    },

    setSelectedIppMerchantCategory(state, action: PayloadAction<string | null>) {
      state.selectedIppMerchantCategory = action.payload
    },

    setSelectedAecsCardMerchantCategory(state, action: PayloadAction<string | null>) {
      state.selectedAecsCardMerchantCategory = action.payload
    },

    setAecsCardCategoryValidationError(state, action: PayloadAction<boolean>) {
      state.aecsCardCategoryValidationError = action.payload
    },

    setMerchantUsingExistingTerminal(state, action: PayloadAction<boolean | null>) {
      state.merchantUsingExistingTerminal = action.payload
    },

    setExistingTerminalAcquirer(state, action: PayloadAction<string | null>) {
      state.existingTerminalAcquirer = action.payload
    },

    setExistingTerminalRentalFrequency(state, action: PayloadAction<string | null>) {
      state.existingTerminalRentalFrequency = action.payload
    },

    setExistingTerminalRentalAmount(state, action: PayloadAction<number | null>) {
      state.existingTerminalRentalAmount = action.payload
    },

    setExistingTerminalMDR(state, action: PayloadAction<string | null>) {
      state.existingTerminalMDR = action.payload
    },

    setIsTaxable(state, action: PayloadAction<boolean>) {
      state.isTaxable = action.payload
    },

    setCanFeeEdited(state, action: PayloadAction<boolean>) {
      state.canFeeEdited = action.payload
    },

    setIsAdvanceMonthLocked(state, action: PayloadAction<boolean>) {
      state.isAdvanceMonthLocked = action.payload
    },

    setOliVasExistence(state, action: PayloadAction<MdrProductState['oliVasExistence']>) {
      state.oliVasExistence = action.payload
    },

    setAvailableSponsoredDevices(state, action: PayloadAction<number>) {
      state.availableSponsoredDevices = action.payload
    },

    setTotalSelectedPosProductsQuantity(state, action: PayloadAction<number>) {
      state.totalSelectedPosProductsQuantity = action.payload
    },

    resetProductState() {
      return initialState
    },
  },
})

export const {
  setProductsLoading,
  setPosProducts,
  setCurrentSelectedPosProduct,
  setPlansLoading,
  setPlanList,
  setCurrentPlan,
  setCurrentPlanBenefit,
  setPlanBenefitList,
  setCurrentSettlementCategory,
  setCurrentAcquire,
  setCurrentSubscribe,
  setViewRatesMdrFeeData,
  updateMainQuantity,
  updateAccessoryQuantity,
  setAccessories,
  setSelectedProductsByVasNameMap,
  setRemainingProductsByVasNameMap,
  resetProductState,
  setAcquirers,
  setRemainingAcquirers,
  setSelectedPosProducts,
  setPosProductInfoForBottomSheet,
  // setSelectedVasPosProducts,
  setEditPosProduct,
  setIsPlanFeeListLoading,
  setIsEditFeesBottomSheetOpen,
  setMainProductBasedOnPlan,
  setIsCreateOliLoading,
  setCustomAdvancedMonth,
  setInterchangePlan,
  setInterchangePlanError,
  setInterchangePlanDisabled,
  setProductMDRFees,
  setAllIssuers,
  setAvailableSponsoredDevices,
  setTotalSelectedPosProductsQuantity,
  setAllInsurancePartners,
  setMccCode,
  setIsFIUUMerchant,
  setFiuuOpportunityId,
  setFiuuOldPlanId,
  setFiuuOldPlanProducts,
  setBillingFrequency,
  setBillingStartDate,
  setGtvAmount,
  setGtvScope,
  setIsGtvCustomised,
  setSelectedIppMerchantCategory,
  setSelectedAecsCardMerchantCategory,
  setAecsCardCategoryValidationError,
  setMerchantUsingExistingTerminal,
  setExistingTerminalAcquirer,
  setExistingTerminalRentalFrequency,
  setExistingTerminalRentalAmount,
  setExistingTerminalMDR,
  setIsTaxable,
  setCanFeeEdited,
  setIsAdvanceMonthLocked,
  setOliVasExistence,
} = mdrProductSlice.actions

export default mdrProductSlice.reducer
